/**
 * AI Engine Client
 *
 * This file creates and configures an Axios instance for communicating
 * with the FastAPI AI engine backend.
 *
 * This is completely separate from Supabase and handles all AI-related operations.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { config } from '../config';
import { addBreadcrumb, captureException } from '../../lib/sentry';
import { handleAIEngineError } from './errorHandler';

/** Generate a RFC-4122 v4 UUID for per-request correlation tracking. */
function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Create axios instance for AI engine
export const aiClient: AxiosInstance = axios.create({
  baseURL: config.ai.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout for AI operations
});

// Request interceptor - Add authentication token and correlation IDs
aiClient.interceptors.request.use(
  async (axiosConfig) => {
    // Attach a unique X-Request-ID to every outgoing request so backend logs
    // can be correlated with frontend console entries end-to-end.
    const requestId = generateRequestId();
    axiosConfig.headers['X-Request-ID'] = requestId;

    // Get current session from Supabase to extract JWT token
    // This is CRITICAL for multi-tenancy - the backend extracts tenant_id from this token
    try {
      const { supabase } = await import('../supabase/client');
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.access_token) {
        axiosConfig.headers.Authorization = `Bearer ${session.access_token}`;

        // Add breadcrumb for debugging
        addBreadcrumb(
          'AI API request with auth token',
          'http',
          'info',
          { hasToken: true, requestId }
        );

        // Structured INFO log — correlates with backend X-Request-ID context
        console.info('[AI Client] ➡️ Request dispatched', {
          requestId,
          method: axiosConfig.method?.toUpperCase(),
          url: axiosConfig.url,
          baseURL: axiosConfig.baseURL,
          fullURL: `${axiosConfig.baseURL}${axiosConfig.url}`,
          hasAuth: true,
        });
      } else {
        // Log warning if no token available - this could indicate auth issues
        console.warn('[AI Client] ⚠️ No authentication token for AI request', { requestId });
        addBreadcrumb(
          'AI API request WITHOUT auth token',
          'http',
          'warning',
          { hasToken: false, requestId }
        );
      }
    } catch (error) {
      console.error('[AI Client] ❌ Failed to get auth token:', error);
      captureException(error as Error, {
        tags: { errorType: 'auth_token_retrieval' },
        level: 'error',
      });
    }

    return axiosConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Capture correlation IDs and handle errors globally
aiClient.interceptors.response.use(
  (response) => {
    // Extract backend-assigned correlation IDs from response headers.
    // The RequestIDMiddleware always echoes these back so we can link
    // frontend console entries to exact backend log lines.
    const backendRequestId = response.headers['x-request-id'];
    const backendTraceId = response.headers['x-trace-id'];

    // Add successful request breadcrumb
    addBreadcrumb(
      `AI API: ${response.config.method?.toUpperCase()} ${response.config.url}`,
      'http',
      'info',
      {
        status: response.status,
        url: response.config.url,
        requestId: backendRequestId,
        traceId: backendTraceId,
      }
    );

    // Structured INFO log — includes backend correlation IDs for RCA
    console.info('[AI Client] ✅ Response received', {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      statusText: response.statusText,
      requestId: backendRequestId,
      traceId: backendTraceId,
    });

    return response;
  },
  async (error: AxiosError) => {
    // Extract correlation IDs from error response headers for cross-service RCA
    const backendRequestId = error.response?.headers?.['x-request-id'];
    const backendTraceId = error.response?.headers?.['x-trace-id'];

    // RBAC: Handle 403 Forbidden - role refresh logic
    if (error.response?.status === 403) {
      console.warn('[AI Client] 🔒 403 Forbidden - refreshing user roles', {
        url: error.config?.url,
        requestId: backendRequestId,
      });

      try {
        // Re-fetch user profile with updated roles
        const { useTenantAuthStore } = await import('../../store/tenantAuthStore');
        await useTenantAuthStore.getState().checkAuth();

        console.info('[AI Client] ✅ User roles refreshed after 403');

        addBreadcrumb(
          'Roles refreshed after 403',
          'auth',
          'info',
          { requestId: backendRequestId }
        );
      } catch (refreshError) {
        console.error('[AI Client] ❌ Failed to refresh roles after 403:', refreshError);
        captureException(refreshError as Error, {
          tags: { errorType: 'role_refresh_failed' },
          level: 'warning',
        });
      }
    }

    // Structured ERROR log with full correlation context
    console.error('[AI Client] ❌ Request failed', {
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      baseURL: error.config?.baseURL,
      fullURL: error.config?.baseURL ? `${error.config.baseURL}${error.config.url}` : error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorCode: error.code,
      errorMessage: error.message,
      responseData: error.response?.data,
      // Correlation IDs for matching with backend logs
      requestId: backendRequestId,
      traceId: backendTraceId,
    });

    // Transform error into user-friendly AIEngineError
    // This handles Sentry reporting, logging, and user message generation
    const aiError = handleAIEngineError(error, {
      operation: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      extra: {
        requestId: backendRequestId,
        traceId: backendTraceId,
      },
    });

    // Reject with the transformed error
    // Components can access aiError.userMessage for display
    return Promise.reject(aiError);
  }
);

export default aiClient;

